import type { TimeConditionPeriod } from '@/types';

const DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
const DAY_LABEL: Record<string, string> = {
  mon: 'Mon',
  tue: 'Tue',
  wed: 'Wed',
  thu: 'Thu',
  fri: 'Fri',
  sat: 'Sat',
  sun: 'Sun',
};
/** JS getUTCDay() index (0 = Sunday) → Yeastar day token. */
const JS_DAY_TOKEN = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

function dayTokens(days?: string): string[] {
  return (days || '')
    .toLowerCase()
    .split(/[\s,]+/)
    .filter(Boolean);
}

/** "mon tue wed thu fri" → "Mon–Fri", "sun sat" → "Sat, Sun", all seven → "Every day". */
export function formatDaysOfWeek(days?: string): string {
  const tokens = dayTokens(days);
  const known = DAY_ORDER.map((d, i) => (tokens.includes(d) ? i : -1)).filter((i) => i >= 0);
  const unknown = tokens.filter((t) => !(t in DAY_LABEL));
  if (known.length === 7) return ['Every day', ...unknown].join(', ');

  const runs: number[][] = [];
  for (const i of known) {
    const last = runs[runs.length - 1];
    if (last && last[last.length - 1] === i - 1) last.push(i);
    else runs.push([i]);
  }
  const label = (i: number) => DAY_LABEL[DAY_ORDER[i]];
  const parts = runs.map((run) =>
    run.length >= 3
      ? `${label(run[0])}–${label(run[run.length - 1])}`
      : run.map(label).join(', ')
  );
  return [...parts, ...unknown].join(', ');
}

function periodTimes(period: TimeConditionPeriod): string[] {
  if (Array.isArray(period.times)) return period.times.filter(Boolean);
  return period.times ? String(period.times).split(/[,\s]+/).filter(Boolean) : [];
}

/** True when the period also limits itself to certain months or days of the month. */
function hasDateLimits(period: TimeConditionPeriod): boolean {
  return Boolean(
    Number(period.time_condition_switch) === 1 && (period.months?.trim() || period.days?.trim())
  );
}

/** "Mon–Fri 08:00–17:00" (plus months / days of month when the PBX uses them). */
export function formatPeriod(period: TimeConditionPeriod): string {
  const days = formatDaysOfWeek(period.days_of_week);
  const times = periodTimes(period)
    .map((t) => t.replace('-', '–'))
    .join(', ');
  const dates = hasDateLimits(period)
    ? [period.months?.trim(), period.days?.trim() ? `days ${period.days.trim()}` : '']
        .filter(Boolean)
        .join(' · ')
    : '';
  return [days, times, dates].filter(Boolean).join(' ') || 'Time period';
}

/** Wall-clock time on the PBX: day token plus minutes since midnight. */
export type PbxClock = { day: string; minutes: number };

/**
 * Parse system/information's system_time ("2026/10/24 11:11:52") using its
 * system_date_format ("YYYY/MM/DD", "MM/DD/YYYY", "DD/MM/YYYY").
 */
export function parsePbxClock(systemTime?: string, dateFormat = 'YYYY/MM/DD'): PbxClock | null {
  const match = (systemTime || '').trim().match(/^(\d+)\D(\d+)\D(\d+)\s+(\d{1,2}):(\d{2})/);
  if (!match) return null;
  const order = dateFormat.toUpperCase().split(/[^A-Z]+/).filter(Boolean);
  const parts: Record<string, number> = {};
  order.forEach((token, i) => {
    parts[token[0]] = Number(match[i + 1]);
  });
  const year = parts.Y;
  const month = parts.M;
  const day = parts.D;
  if (!year || !month || !day) return null;
  const date = new Date(Date.UTC(year, month - 1, day));
  if (Number.isNaN(date.getTime())) return null;
  return {
    day: JS_DAY_TOKEN[date.getUTCDay()],
    minutes: Number(match[4]) * 60 + Number(match[5]),
  };
}

function toMinutes(hhmm: string): number | null {
  const m = hhmm.trim().match(/^(\d{1,2}):(\d{2})$/);
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
}

/** true / false, or null when the period uses month or date limits we cannot check. */
export function periodMatches(period: TimeConditionPeriod, clock: PbxClock): boolean | null {
  if (hasDateLimits(period)) return null;
  if (!dayTokens(period.days_of_week).includes(clock.day)) return false;
  for (const range of periodTimes(period)) {
    const [startRaw, endRaw] = range.split('-');
    const start = toMinutes(startRaw || '');
    let end = toMinutes(endRaw || '');
    if (start == null || end == null) continue;
    if (end === 23 * 60 + 59) end = 24 * 60;
    const inside =
      start <= end
        ? clock.minutes >= start && clock.minutes < end
        : clock.minutes >= start || clock.minutes < end;
    if (inside) return true;
  }
  return false;
}

/**
 * Index of the period the PBX is using now (the first match, top to bottom),
 * -1 when none matches, or null when an earlier period cannot be checked.
 */
export function activePeriodIndex(
  periods: TimeConditionPeriod[],
  clock: PbxClock | null
): number | null {
  if (!clock) return null;
  for (let i = 0; i < periods.length; i++) {
    const match = periodMatches(periods[i], clock);
    if (match === null) return null;
    if (match) return i;
  }
  return -1;
}

/** "6202-IVR-ProshopHours" (current_desination_value) → "6202". */
export function currentDestinationKey(value?: string): string {
  const raw = (value || '').trim();
  const m = raw.match(/^(\d+)-/);
  return m ? m[1] : raw;
}
